import { SubscriptionStatusDTO, WorkspaceRole } from 'models/user'
import { UserAsMemberUsingEmailDTO, WorkspaceAddMembersResultDTO } from 'models/workspaces'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'

import { useQuery } from '@tanstack/react-query'
import { RoleMimetype } from 'constants/API'
import { useAPI } from 'utils/API/useApi'

enum Endpoints {
  CHANGE_WORKSPACE_MEMBER_ROLE = '/workspace/{workspaceId}/changeMemberRole',
  REMOVE_WORKSPACE_MEMBER = '/workspace/{workspaceId}/member',
  RESEND_WORKSPACE_INVITATION = '/workspace/{workspaceId}/resend-invitation/{invitationId}', // under FF allowPendingInvitations  
  WORKSPACE_INVITATION = '/workspace/{workspaceId}/invitation/{invitationId}', // under FF allowPendingInvitations
  ADD_WORKSPACE_MEMBERS = '/workspace/{workspaceId}/members',
  SUBSCRIPTION_STATUS = '/workspace/{workspaceId}/subscription',
}

interface WorkspaceMemberPayload {
  workspaceId: string
  email: string
}

interface InvitationPayload {
  workspaceId: string
  invitationId: string
}

interface ChangeWorkspaceMemberRolePayload {
  auth0Id: string,
  role: WorkspaceRole,
  isRestricted: boolean,
  workspaceId: string
}

// QUERIES

// This query is not used now, but should be back in use in future when BE endpoint is fixed, see MR https://gitlab.com/bkbn/frontend-platform/-/merge_requests/780
// where we started using different endpoint due to issues with endpoint /workspace/{workspaceId}/subscription returning 
// 402 error: Payment Required if users' subscription are canceled.
export function useSubscriptionStatus(workspaceId: string) {
  const api = useAPI<Endpoints>()

  return useQuery({
    queryKey: [EntityKeys.SUBSCRIPTION_STATUS, QueryType.GET, { workspaceId }],
    queryFn: () => api.get<SubscriptionStatusDTO>(Endpoints.SUBSCRIPTION_STATUS, { workspaceId }, false, {
      headers: {
        Accept: RoleMimetype.CLIENT // TODO: Fix on BE so we can send proper accept header
      }
    })
  })
}


// MUTATIONS

export function useAddWorkspaceMembers() {
  const api = useAPI<Endpoints>()

  return getMutation<WorkspaceAddMembersResultDTO, { workspaceId: string, members: UserAsMemberUsingEmailDTO[] }>(
    ({ workspaceId, members }) => api.post(
      Endpoints.ADD_WORKSPACE_MEMBERS,
      { workspaceId },
      [...members],
      false
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

export function useRemoveWorkspaceMember() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, WorkspaceMemberPayload & { inSubscriptionFlow?: boolean }>(
    ({ workspaceId, email, inSubscriptionFlow = false }) => api.delete(
      Endpoints.REMOVE_WORKSPACE_MEMBER,
      { workspaceId },
      false,
      {
        data: {
          email,
          inSubscriptionFlow,
        }
      }
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

/**
 * under FF allowPendingInvitations
 */
export function useRevokeWorkspaceInvitation() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, InvitationPayload>(
    ({ workspaceId, invitationId }) => api.delete(
      Endpoints.WORKSPACE_INVITATION,
      { workspaceId, invitationId },
      false
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

export function useChangeWorkspaceMemberRole() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, ChangeWorkspaceMemberRolePayload>(
    ({ workspaceId, auth0Id, role, isRestricted }) => api.put(
      Endpoints.CHANGE_WORKSPACE_MEMBER_ROLE,
      { workspaceId },
      {
        auth0Id,
        role,
        isRestricted
      },
      false
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

/**
 * under FF allowPendingInvitations
 */
export function useResendWorkspaceInvitation() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, InvitationPayload>(
    ({ workspaceId, invitationId }) => api.post(
      Endpoints.RESEND_WORKSPACE_INVITATION,
      { workspaceId, invitationId },
      {},
      false
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}

/**
 * under FF allowPendingInvitations
 */
export function useAcceptWorkspaceInvitation() {
  const api = useAPI<Endpoints>()

  return getMutation<{}, InvitationPayload>(
    ({ workspaceId, invitationId }) => api.post(
      Endpoints.WORKSPACE_INVITATION,
      { workspaceId, invitationId },
      {},
      false
    ),
    (client) => {
      client.invalidateQueries({ queryKey: [EntityKeys.USER_WORKSPACE, QueryType.LIST_MINE] })
    }
  )
}
